/* sugar:assets/text-buy-button.js@2.0.0 */
/*
 * text-buy-button — the label's live price, and the add to cart behind it.
 *
 * This button has no form of its own: it posts to /cart/add.js and hands the response to the
 * cart drawer. That is why it can promise a one-time purchase — the page's Subscribe toggle
 * cannot reach it. Its label price follows the same rule: the quantity and the offer tier
 * apply, the page's subscription plan does not. A plan set on the button itself still does.
 */
(function () {
  const SUCCESS_DURATION = 1500;

  function mount(island) {
    if (island.dataset.mounted) return;
    island.dataset.mounted = 'true';

    let config;
    try {
      config = JSON.parse(island.textContent);
    } catch (e) {
      return;
    }

    const root = document.querySelector('[data-_sf="' + config.blockId + '"]');
    const btn = root && root.querySelector('[data-text-buy-button]');
    if (!btn || !window.SugarPricing) return;

    const store = window.SugarPricing.store(config);
    const isLocked = btn.hasAttribute('data-locked');
    /*
     * A plan set on the button always wins. Otherwise the merchant decides whether this button is
     * the one-time escape hatch under a subscription-first page, or just follows the page.
     */
    const ownPlanId = btn.dataset.sellingPlan || '';
    /*
     * The button follows the page only when it has no plan of its own and the merchant turned
     * the one-time guarantee off. With its own plan it always prices from the one-time figure,
     * or the page's discount would be counted twice.
     */
    const followsPage = !ownPlanId && config.alwaysOneTime === false;

    /* The button's own subscription plan, which is not the page's. */
    function applyOwnPlan(price) {
      const type = btn.dataset.adjType;
      if (!type) return price;
      const value = parseFloat(btn.dataset.adjValue) || 0;
      if (type === 'percentage') return Math.round((price * (100 - value)) / 100);
      if (type === 'fixed_amount') return Math.max(0, price - value);
      if (type === 'price') return value;
      return price;
    }

    function quantity(state) {
      const forced = btn.dataset.forcedQty;
      if (forced) return Math.max(parseInt(forced, 10) || 1, 1);
      return state.quantity || 1;
    }

    /*
     * A variant the page's main product section picked, for a button that sits outside that
     * section: its own section's store never hears about it, so the button prices it itself.
     */
    let followed = null;

    /* Read fresh on every paint: a sold-out label swaps the price spans out and back. */
    function paint(state) {
      const priceEls = btn.querySelectorAll('[data-tbb-price]');
      const compareEls = btn.querySelectorAll('[data-tbb-compare]');
      const regularEls = btn.querySelectorAll('[data-tbb-regular]');
      if (!priceEls.length && !compareEls.length && !regularEls.length) return;

      const qty = quantity(state);
      const ownPlan = !!btn.dataset.adjType;
      let total;
      let compareTotal;

      if (!isLocked && !ownPlan && !followed && qty === state.quantity) {
        /*
         * The button is buying exactly what the page is buying, so take the store's totals whole.
         * Rebuilding them from a rounded per-unit price drifts a cent on quantities that don't
         * divide evenly.
         */
        total = followsPage ? state.totalPrice : state.oneTimeTotal;
        compareTotal = followsPage ? state.totalCompare : state.oneTimeCompare;
      } else {
        /*
         * A locked variant, a plan of its own, or a forced quantity: this button is buying
         * something the page isn't, so it prices per unit.
         */
        let base = isLocked ? parseInt(btn.dataset.basePrice, 10) || 0 : state.baseUnit;
        let baseCompare = isLocked ? parseInt(btn.dataset.baseCompare, 10) || 0 : state.baseCompareUnit;
        let pageUnit = followsPage ? state.unitPrice : state.oneTimeUnit;
        if (followed && !isLocked) {
          base = followed.price;
          baseCompare = followed.compare;
          pageUnit = followed.price;
        }
        const per = applyOwnPlan(isLocked ? base : pageUnit);
        /*
         * Show a compare price when something actually brought the price below the variant's
         * own, otherwise fall back to the variant's compare-at.
         */
        const compare = per < base ? base : baseCompare > base ? baseCompare : 0;
        total = per * qty;
        compareTotal = compare * qty;
      }

      /*
       * [regular_price]: the variant this button adds at its own price, for the quantity it adds,
       * before any offer discount or plan. The page's variant follows the offer tier the shopper
       * picks, so a "buy once" link under the tiers reads that tier's full price.
       */
      const regularUnit = isLocked ? parseInt(btn.dataset.basePrice, 10) || 0 : followed ? followed.price : state.baseUnit;

      const money = (cents) => window.SugarPricing.formatMoney(cents, config.format, !!config.hideCents && cents % 100 === 0);
      const show = compareTotal > total;

      priceEls.forEach((el) => {
        el.textContent = money(total);
      });
      compareEls.forEach((el) => {
        el.hidden = !show;
        if (show) el.textContent = money(compareTotal);
      });
      /* A store still waiting for the page to finish loading has no unit yet: keep the server's. */
      if (regularUnit > 0) {
        regularEls.forEach((el) => {
          el.textContent = money(regularUnit * qty);
        });
      }
    }

    let stop;
    stop = store.subscribe(function (state) {
      /* A re-rendered section leaves this copy detached; the fresh island has taken over. */
      if (!btn.isConnected) {
        if (stop) stop();
        return;
      }
      paint(state);
    });

    /*
     * Sold out and unavailable, the way the Product Buy Button shows them: the button is
     * disabled and its first line reads the block's sold-out text or the theme's
     * "Unavailable". The label it gives way to is kept (rendered into a template when the page
     * opened on a sold-out variant) and comes back, prices repainted, once the variant can be
     * bought again.
     */
    const textEl = btn.querySelector('[data-tbb-text]');
    const labelTpl = root.querySelector('template[data-tbb-label]');
    let buyableLabel = labelTpl ? labelTpl.innerHTML : textEl ? textEl.innerHTML : '';
    let shownState = btn.disabled ? (btn.classList.contains('is-sold-out') ? 'sold' : 'missing') : 'ok';
    function setAvailability(next) {
      if (next === shownState || !textEl) return;
      if (shownState === 'ok') buyableLabel = textEl.innerHTML;
      shownState = next;
      btn.disabled = next !== 'ok';
      btn.classList.toggle('is-sold-out', next === 'sold');
      if (next === 'ok') {
        textEl.innerHTML = buyableLabel;
        paint(store.state);
      } else {
        textEl.textContent = next === 'sold' ? btn.dataset.soldOutText || 'Sold Out' : btn.dataset.unavailableText || 'Unavailable';
      }
    }

    /*
     * Which variant changes the button follows: its own section's, and the page's main product
     * section's when the button sits outside it, is for the same product and its own section
     * picks no variant of its own. The product check is the variant ids: a button for another
     * product never matches the main section's variants. A Custom Sticky bar passes the main
     * section's variant on under its own section id (assets/sticky-custom.js), so a button in
     * one already hears it.
     */
    const ownSectionId = String(config.sectionId);
    const ownSection = () => root.closest('.shopify-section');
    /* The Product Main section selling this button's product; a Featured Product section is never it. */
    function mainSectionId() {
      const host = document.querySelector('product-info.sugar-product-main[data-product-id="' + config.productId + '"]');
      return host ? String(host.dataset.section || '') : '';
    }
    function follows(sectionId) {
      if (!sectionId || sectionId === ownSectionId) return true;
      if (sectionId !== mainSectionId()) return false;
      const section = ownSection();
      return !(section && section.querySelector('product-info[data-main-product], .sugar-product-variant-picker, [data-ov], sticky-custom'));
    }

    /*
     * The variant the button will add, which the store doesn't own: whatever the picker or an
     * offer card last chose. The ids arrive from Liquid as numbers and an event's id can be
     * either, so both sides compare as strings (comparing a string against the numbers never
     * matched, and the button kept adding the variant the page opened with).
     */
    const ownVariantIds = (config.variantIds || []).map(String);
    document.addEventListener('variant:change', function (e) {
      const detail = e.detail || {};
      const sectionId = detail.sectionId ? String(detail.sectionId) : '';
      const v = detail.variant;
      if (!v || v.id == null || isLocked || !btn.isConnected) return;
      if (ownVariantIds.length && ownVariantIds.indexOf(String(v.id)) === -1) return;
      if (!follows(sectionId)) return;
      btn.dataset.variantId = String(v.id);
      followed = sectionId && sectionId !== ownSectionId
        ? { price: parseInt(v.price, 10) || 0, compare: parseInt(v.compare_at_price, 10) || 0 }
        : null;
      setAvailability(v.available === false ? 'sold' : 'ok');
      if (followed) paint(store.state);
    });

    /*
     * A picked combination no variant has: the product section empties its form's variant
     * input and announces no variant, so that empty input is the signal.
     */
    document.addEventListener('change', function (e) {
      const input = e.target;
      if (isLocked || !btn.isConnected || !input || input.name !== 'id' || input.value !== '') return;
      const form = input.form;
      /* getAttribute: the form's own input named "id" shadows form.id */
      const match = form && /^product-form-(.+)$/.exec(form.getAttribute('id') || '');
      if (!match || !follows(match[1])) return;
      const host = form.closest('product-info[data-main-product]');
      if (host && String(host.dataset.productId) !== String(config.productId)) return;
      setAvailability('missing');
    });

    wireAddToCart(btn, store, {
      quantity,
      /* Its own plan, else the page's when the merchant asked it to follow, else one-time. */
      sellingPlan: () => ownPlanId || (followsPage ? store.state.sellingPlanId || '' : ''),
    });
  }

  /* ------------------------------------------------------------ add to cart */

  async function getUpgradedCart() {
    const el = document.querySelector('sugar-cart-drawer');
    if (!el) return null;
    if (customElements.get('sugar-cart-drawer') && typeof el.getSectionsToRender === 'function') return el;
    try {
      await Promise.race([
        customElements.whenDefined('sugar-cart-drawer'),
        new Promise((_, reject) => setTimeout(reject, 2000)),
      ]);
      customElements.upgrade(el);
    } catch (e) {
      /* The drawer never upgraded; fall through and let the caller cope. */
    }
    return el;
  }

  function wireAddToCart(btn, store, options) {
    const quantity = options.quantity;
    let submitting = false;

    btn.addEventListener('click', async function (e) {
      e.preventDefault();
      if (submitting || btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;
      // A required Personalization field that is empty stops the buy and points at it.
      const cartExtras = window.SugarCart && window.SugarCart.extras;
      if (cartExtras && typeof cartExtras.validate === 'function' && !cartExtras.validate(btn, [{ id: btn.dataset.variantId }])) return;
      submitting = true;

      btn.setAttribute('aria-disabled', 'true');
      btn.classList.add('loading');
      const spinner = btn.querySelector('.loading__spinner');
      if (spinner) spinner.classList.remove('hidden');

      const sellingPlan = options.sellingPlan();
      const skipCart = btn.dataset.skipCart === 'true';
      const successText = btn.dataset.successText || '';

      const item = { id: parseInt(btn.dataset.variantId, 10), quantity: quantity(store.state) };
      if (sellingPlan) item.selling_plan = parseInt(sellingPlan, 10);

      const drawerEl = document.querySelector('sugar-cart-drawer');
      // Lines other blocks queued for this buy (Product Upsells)
      const extras = window.SugarCart && window.SugarCart.extras ? window.SugarCart.extras.collect(btn) : [];
      // A button that buys exactly what the page is buying also carries the selected tier's
      // gifts, read from the offers controller in its section (or the main product section
      // when this button sits in a sticky bar).
      const gifts = [];
      if (!btn.hasAttribute('data-locked') && !btn.dataset.adjType && !btn.dataset.forcedQty) {
        const section = btn.closest('.shopify-section');
        let offersRoot = section && section.querySelector('[data-sugar-offers], .offers');
        if (!offersRoot && btn.closest('sticky-custom, sticky-product')) {
          const mainForm = document.querySelector('product-form form, form[action*="/cart/add"]');
          const mainSection = mainForm && mainForm.closest('.shopify-section');
          if (mainSection && mainSection !== section) offersRoot = mainSection.querySelector('[data-sugar-offers], .offers');
        }
        const controller = offersRoot && offersRoot.sugarOffers;
        if (controller && typeof controller.giftLines === 'function') {
          try { gifts.push(...controller.giftLines()); } catch (e) { /* no tier selected */ }
        }
      }
      const body = { items: [item].concat(gifts, extras) };
      if (cartExtras && typeof cartExtras.decorate === 'function') cartExtras.decorate(btn, body.items);
      if (drawerEl && typeof drawerEl.getSectionsToRender === 'function') {
        body.sections = drawerEl.getSectionsToRender().map((s) => s.id);
        body.sections_url = window.location.pathname;
      }

      const request = cartExtras && typeof cartExtras.request === 'function'
        ? cartExtras.request(body)
        : { body: JSON.stringify(body), contentType: 'application/json' };
      try {
        const res = await fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
          method: 'POST',
          headers: request.contentType ? { 'Content-Type': request.contentType, Accept: 'application/json' } : { Accept: 'application/json' },
          body: request.body,
        });
        const data = await res.json();

        if (data.status) {
          let message = data.description || data.message || 'Failed to add to cart';
          if (typeof message === 'object') message = Object.values(message).flat().join(', ');
          throw new Error(message);
        }

        if (extras.length) window.SugarCart.extras.commit(btn, data);

        if (skipCart) {
          window.location.href = '/checkout';
          return;
        }

        btn.classList.remove('loading');
        btn.removeAttribute('aria-disabled');
        if (spinner) spinner.classList.add('hidden');

        const textEl = btn.querySelector('[data-tbb-text]');
        if (successText && textEl) {
          const original = textEl.innerHTML;
          textEl.innerHTML = successText;
          setTimeout(() => {
            textEl.innerHTML = original;
          }, SUCCESS_DURATION);
        }

        const cart = await getUpgradedCart();
        if (cart) {
          let sections = data.sections;
          if (!sections) {
            const ids = (cart.sectionId || 'sugar-cart-drawer') + ',cart-icon-bubble';
            const cartRes = await fetch(window.routes.cart_url + '?sections=' + encodeURIComponent(ids));
            sections = await cartRes.json();
          }
          if (typeof cart.renderContents === 'function') {
            cart.renderContents({ id: data.product_id, sections });
          } else if (typeof cart.open === 'function') {
            cart.open();
          }
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: btn.dataset.variantId,
            cartData: data,
          });
        }
      } catch (err) {
        console.error('[TextBuyButton]', err.message);
      } finally {
        btn.classList.remove('loading');
        btn.removeAttribute('aria-disabled');
        if (spinner) spinner.classList.add('hidden');
        submitting = false;
      }
    });
  }

  function init() {
    document.querySelectorAll('script[data-sugar-text-buy-button]').forEach(mount);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
